// Generative-UI canvas surface (TASK-493, ports SixEase shell/Canvas, simplified —
// no container queries / scroll-follow / density prefs). Reads canvasStore (single
// sink) and renders the bento grid of non-rail tiles. Pinned (rail:"left") tiles
// dock in the DO column (TASK-575), not here, so this is grid-only.
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import ArtifactErrorBoundary from '../dock/artifacts/ArtifactErrorBoundary';
import { WidgetRenderer } from '../../registry/WidgetRenderer';
import { useCanvasStore, selectHasUnsavedCanvas } from '../../stores/canvasStore';
import { useCanvasUrlSync } from '../../hooks/useCanvasUrlSync';
import { CanvasRestoreNoticeBanner } from './CanvasRestoreNotice';
import { getDockChatStore } from '../../stores/dockChatStores';
import { useDockStore } from '../../stores/dockStore';
import { useAccount } from '../../contexts/AccountContext';
import { CANVAS_PLACEMENT, canvasScope, threadKeyForScope, tileBelongsToThread } from './canvasThread';
import type { CanvasTileSpec } from '../../registry/types';
import { CanvasTile } from './CanvasTile';
import { WidgetSkeleton } from './WidgetSkeleton';
import { PREP_STARTER } from './InputDock';

// TASK-650: what an RM can ask on an empty canvas — clicking hands the prompt to
// the InputDock (the one submit pipeline) via canvasStore.requestPrompt.
//
// Deep review #5: this list is the FIRST thing an RM sees, and it still said "…with Huber"
// after TASK-670 retargeted the same starter in `InputDock.QUICK_COMMANDS` — Huber is the one
// demo client with no meeting session, so the most prominent path into MeetingPrep landed on
// the degraded widget. The prep starter is now shared from the dock rather than duplicated,
// so the next retarget cannot miss one of the two copies.
const STARTERS = [
  'Which clients need attention?',
  "Show Schneider's portfolio",
  PREP_STARTER,
  'Show my client book',
] as const;

export function Canvas() {
  // TASK-836: the canvas is addressable. Mounted here rather than in `DashboardPage` because
  // this same <Canvas /> renders inside `CanvasDrawer` on every non-dashboard route, and the
  // address should follow the canvas wherever it is shown.
  const { restoreNotice, dismissRestoreNotice } = useCanvasUrlSync();
  const specs = useCanvasStore((s) => s.specs);
  const pendingStatus = useCanvasStore((s) => s.pendingStatus);
  const toggleCollapse = useCanvasStore((s) => s.toggleCollapse);
  const setRail = useCanvasStore((s) => s.setRail);
  const closeTile = useCanvasStore((s) => s.closeTile);
  const clearCanvas = useCanvasStore((s) => s.clear);
  const requestPrompt = useCanvasStore((s) => s.requestPrompt);
  const hasPromptConsumer = useCanvasStore((s) => s.promptConsumers > 0);
  // TASK-651 lifecycle: undo, named workstreams, active scope.
  const undoCanvas = useCanvasStore((s) => s.undo);
  // TASK-795: the canvas conversation is scoped now — placement, or the task
  // thread that owns the work. Resolved through the shared helper so this and
  // InputDock cannot disagree about which thread is on screen.
  const { account } = useAccount();
  const activeTaskThread = useDockStore((s) => s.activeTaskThread);
  // This same <Canvas /> renders inside CanvasDrawer on every non-dashboard
  // route, where the canvas InputDock is not mounted and the route's own
  // placement owns the conversation. Attributing bubbles to a task thread there
  // would put client A's task transcript on client B's page — the failure
  // TASK-787's allowlist exists to prevent — so off the dashboard the drawer
  // shows the canvas's own thread and never a task's.
  // `hasPromptConsumer` is exactly "the canvas InputDock is mounted here"
  // (TASK-768) — the same signal, not a second route check that could drift.
  const scope = useMemo(
    () => (hasPromptConsumer ? canvasScope(activeTaskThread) : CANVAS_PLACEMENT),
    [activeTaskThread, hasPromptConsumer]
  );
  const activeThreadKey = threadKeyForScope(scope);
  const accountId = account?.id ?? 'unknown';
  const chatStore = useMemo(
    () => getDockChatStore(accountId, scope),
    [accountId, scope.scopeType, scope.entityId] // eslint-disable-line react-hooks/exhaustive-deps
  );
  // TASK-712 (history-desync fix): the dock's LLM thread resets WITH the bubbles.
  // The model must never keep remembering a conversation the RM just cleared —
  // and a restored canvas starts a fresh thread rather than replaying a stale one.
  //
  // TASK-795: `canvasStore.clear()` wipes EVERY tile, including the bubbles of a
  // thread that is merely filtered off-screen — so resetting only the active
  // store would leave the other conversation remembered behind bubbles that no
  // longer exist, which is the exact desync TASK-712 fixed. Reset every thread
  // whose bubbles this just destroyed: the canvas's own, and the task thread if
  // one is on screen.
  const resetThreadsOnScreen = () => {
    chatStore.getState().reset();
    const base = getDockChatStore(accountId, CANVAS_PLACEMENT);
    if (base !== chatStore) base.getState().reset();
  };
  const clear = () => {
    clearCanvas();
    resetThreadsOnScreen();
  };
  const undo = () => {
    undoCanvas();
    resetThreadsOnScreen();
  };
  const canUndo = useCanvasStore((s) => s.history.length > 0);
  const workstreams = useCanvasStore((s) => s.workstreams);
  const activeWorkstream = useCanvasStore((s) => s.activeWorkstream);
  const saveWorkstream = useCanvasStore((s) => s.saveWorkstream);
  const openWorkstream = useCanvasStore((s) => s.openWorkstream);
  const deleteWorkstream = useCanvasStore((s) => s.deleteWorkstream);
  const loadWorkstreams = useCanvasStore((s) => s.loadWorkstreams);
  // TASK-876: "navigating away silently discards an unsaved canvas". A `beforeunload`
  // handler is the only hook the browser gives for a reload, a closed tab or a typed URL
  // — the three exits that lose the canvas outright. It is registered ONLY while there is
  // unsaved work, because a listener that is always attached makes some browsers prompt
  // on every navigation and teaches the advisor to dismiss it unread.
  const hasUnsaved = useCanvasStore(selectHasUnsavedCanvas);
  useEffect(() => {
    if (!hasUnsaved) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Browsers show their own wording and ignore any string given here; returning a
      // value is what still triggers the prompt.
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsaved]);
  // TASK-876: the list comes from the account, not this browser — so it has to be
  // fetched. Once per mount: a saved canvas changes rarely and re-fetching on every
  // render would put a request behind every canvas interaction.
  useEffect(() => {
    void loadWorkstreams();
  }, [loadWorkstreams]);
  const [naming, setNaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [wsMenuOpen, setWsMenuOpen] = useState(false);
  const [maximizedId, setMaximizedId] = useState<string | null>(null);

  // TASK-650: Escape restores a maximized tile — predictable close behavior.
  useEffect(() => {
    if (!maximizedId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMaximizedId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [maximizedId]);

  // Pinned (rail:"left") tiles render in the DO column; the grid is the rest.
  //
  // TASK-795 / DEC-6: chat bubbles belong to a conversation, so only the active
  // thread's are shown. Widget tiles are the advisor's arranged work and survive
  // a thread switch untouched — the grid is a workspace, not a transcript.
  const grid = specs.filter((t) => !t.rail && tileBelongsToThread(t, activeThreadKey));

  // Auto-follow (TASK-498): when a summon appends tiles, scroll the newest into
  // view so it never lands silently off-screen. Additions only, not initial mount.
  const prevCount = useRef(grid.length);
  const lastTileId = grid.length > 0 ? grid[grid.length - 1].id : null;
  useEffect(() => {
    if (grid.length > prevCount.current && lastTileId) {
      document.getElementById(lastTileId)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    prevCount.current = grid.length;
  }, [grid.length, lastTileId]);

  // Conversational specs (TASK-499) render full-width with no tile chrome, so dialogue
  // bubbles interleave with widget tiles by insertion order (mirrors SixEase Canvas BARE).
  const BARE = new Set(['ChatMessage']);

  const renderTile = (tile: CanvasTileSpec) =>
    BARE.has(tile.component) ? (
      <div key={tile.id} id={tile.id} className="col-span-full">
        <ArtifactErrorBoundary artifactId={tile.id}>
          <WidgetRenderer spec={tile} />
        </ArtifactErrorBoundary>
      </div>
    ) : (
    <CanvasTile
      key={tile.id}
      tile={tile}
      maximized={maximizedId === tile.id}
      onToggleCollapse={toggleCollapse}
      onToggleMaximize={(id) => setMaximizedId((cur) => (cur === id ? null : id))}
      onSetRail={(id, rail) => {
        // Pinning removes the tile from the grid — a still-set maximizedId would
        // strand the backdrop overlay with no tile in it.
        if (maximizedId === id) setMaximizedId(null);
        setRail(id, rail);
      }}
      onClose={(id) => {
        if (maximizedId === id) setMaximizedId(null);
        closeTile(id);
      }}
    >
      <ArtifactErrorBoundary artifactId={tile.id}>
        <WidgetRenderer spec={tile} />
      </ArtifactErrorBoundary>
    </CanvasTile>
  );

  // grid, not specs: with every tile pinned to the rail the grid is empty too.
  // An in-flight summon still shows its skeleton (TASK-601), never the empty state.
  if (grid.length === 0 && !pendingStatus) {
    return (
      <div>
        {/* TASK-842: rendered in BOTH branches. A param that could not be read at all
            restores nothing, so it lands on the empty state — the one place where an
            unexplained absence looks most like a normal canvas. */}
        <CanvasRestoreNoticeBanner notice={restoreNotice} onDismiss={dismissRestoreNotice} />
      <div
        data-testid="canvas-empty-state"
        className="rounded-2xl border border-dashed border-border p-8 text-center"
      >
        <p className="text-sm font-medium text-foreground">
          Ask for any client, portfolio, or compliance view
        </p>
        {/* TASK-768: `requestPrompt` is consumed only by the canvas InputDock,
            which mounts on /dashboard. Inside the CanvasDrawer on any other
            route these chips could not fire — and a visible control that does
            nothing is worse than not offering one. Both the invitation to type
            and the starters are gated on a consumer actually being mounted. */}
        {hasPromptConsumer ? (
          <>
            <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
              Type below in plain language — the canvas builds the right view and keeps your
              conversation alongside it. Try one of these:
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {STARTERS.map((starter) => (
                <button
                  key={starter}
                  type="button"
                  onClick={() => requestPrompt(starter)}
                  data-testid="canvas-starter"
                  className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-accent"
                >
                  {starter}
                </button>
              ))}
            </div>
          </>
        ) : (
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Views you summon from the copilot appear here.
          </p>
        )}
        {/* TASK-651: prior work stays recoverable from the empty state. */}
        {(canUndo || workstreams.length > 0) && (
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2 border-t border-border pt-4">
            {canUndo && (
              <button
                type="button"
                onClick={undo}
                data-testid="canvas-undo"
                className="rounded-full border border-border px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-accent"
              >
                ↩ Restore previous canvas
              </button>
            )}
            {workstreams.map((w) => (
              <button
                key={w.name}
                type="button"
                onClick={() => openWorkstream(w.name)}
                // TASK-876: a shared list needs to say whose it is. `owner` and `v` only
                // appear for a server-side workstream; a legacy device-local one says so
                // instead of implying it is visible to anyone else.
                title={[
                  `Saved ${new Date(w.savedAt).toLocaleString()}`,
                  w.clientName,
                  w.owner ? `by ${w.owner}` : null,
                  w.version && w.version > 1 ? `v${w.version}` : null,
                  w.local ? 'saved on this device only — re-save to share it' : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                className="rounded-full bg-accent px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                ⧉ {w.name}
              </button>
            ))}
          </div>
        )}
      </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <CanvasRestoreNoticeBanner notice={restoreNotice} onDismiss={dismissRestoreNotice} />
      {/* TASK-651: workstream toolbar — scope on the left, lifecycle on the right. */}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
          {activeWorkstream && (
            <span
              data-testid="active-workstream"
              className="rounded-full bg-accent px-2 py-0.5 font-medium text-foreground"
            >
              ⧉ {activeWorkstream}
            </span>
          )}
          {(() => {
            const scoped = [...grid].reverse().find((t) => typeof t.props?.clientName === 'string');
            return scoped ? (
              <span className="truncate rounded-full border border-border px-2 py-0.5">
                Client: {String(scoped.props.clientName)}
              </span>
            ) : null;
          })()}
        </div>
        <div className="relative flex items-center gap-2 text-xs">
          {canUndo && (
            <button
              type="button"
              onClick={undo}
              data-testid="canvas-undo"
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              ↩ undo
            </button>
          )}
          {naming ? (
            <form
              className="flex items-center gap-1"
              onSubmit={(e) => {
                e.preventDefault();
                // TASK-876: saving is a network call now — the form closes only once the
                // server has the workstream. Closing it optimistically would tell the
                // advisor their canvas was handed over before anything was written.
                void (async () => {
                  if (await saveWorkstream(nameDraft)) {
                    setNaming(false);
                    setNameDraft('');
                  } else {
                    toast.error('Could not save the workstream. Please try again.');
                  }
                })();
              }}
            >
              <input
                autoFocus
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Escape' && setNaming(false)}
                placeholder="Workstream name…"
                aria-label="Workstream name"
                className="h-6 w-40 rounded border border-border bg-background px-2 text-xs outline-none focus:ring-1 focus:ring-primary"
              />
              <button type="submit" data-testid="confirm-save-workstream" className="text-primary hover:underline">
                Save
              </button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setNaming(true)}
              data-testid="save-workstream"
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              ⧉ save workstream
            </button>
          )}
          {workstreams.length > 0 && (
            <button
              type="button"
              onClick={() => setWsMenuOpen((v) => !v)}
              aria-expanded={wsMenuOpen}
              data-testid="workstreams-menu"
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              recent ▾
            </button>
          )}
          {wsMenuOpen && (
            <div className="absolute right-0 top-6 z-20 w-64 rounded-lg border border-border bg-popover p-1 shadow-md">
              {workstreams.map((w) => (
                <div key={w.name} className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      openWorkstream(w.name);
                      setWsMenuOpen(false);
                    }}
                    className="flex-1 truncate rounded px-2 py-1.5 text-left text-xs text-foreground hover:bg-accent"
                    title={`Saved ${new Date(w.savedAt).toLocaleString()}`}
                  >
                    {w.name}
                    {w.clientName && (
                      <span className="ml-1 text-muted-foreground">· {w.clientName}</span>
                    )}
                  </button>
                  <button
                    type="button"
                    aria-label={`Delete workstream ${w.name}`}
                    onClick={() => void deleteWorkstream(w.name)}
                    className="rounded px-1.5 py-1 text-muted-foreground hover:text-destructive"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={clear}
            data-testid="canvas-clear"
            title="Clear the canvas — recoverable with undo during this session"
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            × clear canvas
          </button>
        </div>
      </div>

      {/* grid-flow-dense (TASK-601): sm tiles backfill the holes mixed spans leave */}
      <div className="grid grid-flow-dense grid-cols-1 content-start items-start gap-4 lg:grid-cols-2 xl:grid-cols-3">
        {grid.map(renderTile)}
        {pendingStatus && <WidgetSkeleton status={pendingStatus} />}
      </div>

      {maximizedId && (
        <div
          className="fixed inset-0 z-30 bg-black/30"
          onClick={() => setMaximizedId(null)}
          aria-hidden
        />
      )}
    </div>
  );
}
